<template>
  <div>
    <div v-if="!props.franchiseId" class="so-catalog-fiscal-hint">
      {{ t('Selecione uma franquia para ver as pendências fiscais de serviços adicionais.') }}
    </div>

    <template v-else>
      <div v-if="selectedRows.length > 0" class="selection-toolbar">
        <span class="selection-count" data-testid="so-catalog-fiscal-additional-service-selection-count">
          {{ selectedRows.length }} {{ t('selecionados') }}
        </span>
        <app-button
          :label="t('Aplicar LC116 aos selecionados')"
          icon="pi pi-bolt"
          severity="info"
          outlined
          size="small"
          data-testid="so-catalog-fiscal-additional-service-bulk-apply-btn"
          @click="openBulkModal"
        />
        <span
          v-if="selectedRows.length > MAX_BULK_SELECTION"
          class="selection-large-notice"
          data-testid="so-catalog-fiscal-additional-service-bulk-large-selection-notice"
        >
          {{ t('Mais de 200 itens selecionados — serão aplicados um a um, o que pode levar alguns minutos.') }}
        </span>
      </div>

      <progress-bar
        v-if="bulkRunning"
        :value="bulkProgressPercent"
        data-testid="so-catalog-fiscal-additional-service-bulk-progress"
      />

      <div v-if="loadError" data-testid="so-catalog-fiscal-error" class="so-catalog-fiscal-error">
        {{ loadError }}
      </div>
      <div v-else-if="!isLoading && rows.length === 0" data-testid="so-catalog-fiscal-empty" class="so-catalog-fiscal-empty">
        {{ t('Nenhuma pendência fiscal de serviço adicional para esta franquia.') }}
      </div>
      <data-table
        v-else
        ref="datatableRef"
        v-model:selection="selectedRows"
        :config="datatableConfig"
        :data="rows"
        @inline-save="handleInlineSave"
        @edit-click="handleEditClick"
      />
    </template>

    <SOCatalogFiscalBulkApplyModal
      ref="bulkModalRef"
      kind="additional_service"
      :scope="props.scope"
      @apply="handleBulkApply"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useSOCatalogFiscal, type FiscalScope } from '../../composables/useSOCatalogFiscal';
import { isValidLc116Code } from './fiscalCodeValidation';
import SOCatalogFiscalBulkApplyModal from './SOCatalogFiscalBulkApplyModal.vue';

type Id = number | string;

interface AdditionalServiceMissingRow {
  so_additional_service_id: number;
  so_additional_service_name: string;
  so_additional_service_fiscal_lc116_code?: string;
  [key: string]: unknown;
}

interface DataTableInstance {
  loadData?: () => void;
}

interface InlineSavePayload {
  data: AdditionalServiceMissingRow;
  isNew: boolean;
}

const MAX_BULK_SELECTION = 200;

const props = defineProps<{
  franchiseId?: number;
  scope: FiscalScope;
}>();

const { t } = useI18n();
const {
  carregarPendenciasServicosAdicionais,
  salvarAdditionalServiceFiscal,
  isLoading,
} = useSOCatalogFiscal();

const datatableRef = ref<DataTableInstance | null>(null);
const bulkModalRef = ref<{ open: (ids: Id[]) => void } | null>(null);
const rows = ref<AdditionalServiceMissingRow[]>([]);
const selectedRows = ref<AdditionalServiceMissingRow[]>([]);
const loadError = ref<string>('');
const bulkRunning = ref(false);
const bulkDone = ref(0);
const bulkTotal = ref(0);

const bulkProgressPercent = computed<number>(() =>
  bulkTotal.value === 0 ? 0 : Math.round((bulkDone.value / bulkTotal.value) * 100)
);

const tableColumns = computed(() => [
  { data: 'so_additional_service_id', title: t('ID'), width: 80, sortable: true, filterType: 'text', renderType: 'clickable_id', editable: false },
  { data: 'so_additional_service_name', title: t('Serviço adicional'), width: 260, sortable: true, filterType: 'text', editable: false },
  { data: 'so_additional_service_fiscal_lc116_code', title: t('Código LC116'), width: 160, editType: 'text' },
]);

const datatableConfig = computed(() => ({
  tableId: 'so-catalog-fiscal-additional-services',
  rowTestId: (data: Record<string, unknown>) => {
    const id = (data as AdditionalServiceMissingRow)?.so_additional_service_id;
    return id == null ? '' : `so-catalog-fiscal-additional-service-row-${id}`;
  },
  dataKey: 'so_additional_service_id',
  columns: tableColumns.value,
  pageLength: 25,
  pagination: true,
  sortable: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  inlineEditing: true,
  inlineAddButton: false,
  checkbox: { enabled: true, position: 'first' },
  rowSelection: 'multiple',
}));

const loadData = async (): Promise<void> => {
  selectedRows.value = [];
  loadError.value = '';
  try {
    rows.value = (await carregarPendenciasServicosAdicionais(props.franchiseId)) as AdditionalServiceMissingRow[];
  } catch (err: unknown) {
    logError('SOCatalogFiscalAdditionalServicesTable', 'loadData', 'Error', err);
    loadError.value = err instanceof Error ? err.message : t('Erro ao carregar pendências fiscais de serviços adicionais');
    rows.value = [];
  }
};

const handleEditClick = (additionalServiceId: Id): void => {
  log('SOCatalogFiscalAdditionalServicesTable', 'handleEditClick', 'Opening modal', additionalServiceId);
  eventBus.emit('modal.so-catalog-fiscal.open', {
    kind: 'additional_service',
    id: additionalServiceId,
    franchiseId: props.franchiseId,
    scope: props.scope,
  });
};

const handleInlineSave = async (event: InlineSavePayload): Promise<void> => {
  const { data } = event;
  if (!isValidLc116Code(data.so_additional_service_fiscal_lc116_code)) {
    // Deliberately no loadData() here: it would refetch from the backend and
    // overwrite what the user just typed. The row's underlying object was
    // already mutated in place by the inline editor, so it stays visible
    // as-is — the user can correct it and retry.
    eventBus.emit(
      'globalMessage.error',
      t('Código LC116 inválido — use o formato NN.NN (ex: 14.01)')
    );
    return;
  }
  try {
    await salvarAdditionalServiceFiscal({
      additionalServiceId: Number(data.so_additional_service_id),
      scope: props.scope,
      franchiseId: props.franchiseId,
      fields: { lc116_code: data.so_additional_service_fiscal_lc116_code },
    });
    eventBus.emit('globalMessage.success', t('Código LC116 salvo com sucesso'));
    eventBus.emit('so-catalog-fiscal.saved');
  } catch (err: unknown) {
    logError('SOCatalogFiscalAdditionalServicesTable', 'handleInlineSave', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar código LC116') + ': ' + message);
    await loadData();
  }
};

const openBulkModal = (): void => {
  const ids = selectedRows.value.map((r) => r.so_additional_service_id);
  bulkModalRef.value?.open(ids);
};

const handleBulkApply = async ({ ids, code }: { ids: Id[]; code: string }): Promise<void> => {
  bulkTotal.value = ids.length;
  bulkDone.value = 0;
  bulkRunning.value = true;
  const failedNames: string[] = [];

  // Sequential, on purpose: N POSTs, each rebuilding the fiscal model
  // (sync_table + an information_schema.STATISTICS check per request) — the
  // host has 4 vCPUs, so Promise.all would just contend for the same DB
  // connection pool with no real gain.
  for (const id of ids) {
    try {
      await salvarAdditionalServiceFiscal({
        additionalServiceId: Number(id),
        scope: props.scope,
        franchiseId: props.franchiseId,
        fields: { lc116_code: code },
      });
    } catch (err: unknown) {
      logError('SOCatalogFiscalAdditionalServicesTable', 'handleBulkApply', 'Error on id', { id, err });
      const row = rows.value.find((r) => String(r.so_additional_service_id) === String(id));
      failedNames.push(row?.so_additional_service_name || String(id));
    }
    bulkDone.value += 1;
  }

  bulkRunning.value = false;
  const successCount = bulkTotal.value - failedNames.length;
  eventBus.emit('globalMessage.success', `${successCount} ${t('de')} ${bulkTotal.value} ${t('serviços adicionais salvos com sucesso')}`);
  if (failedNames.length > 0) {
    eventBus.emit('globalMessage.error', t('Falha ao salvar os seguintes serviços adicionais') + ': ' + failedNames.join(', '));
  }

  await loadData();
};

const handleFilterUpdated = (): void => {
  loadData();
};
const handleSaved = (): void => {
  loadData();
};

// No 'so-catalog-fiscal.deleted' listener: this domain has no delete
// affordance (see useSOCatalogFiscal.ts — the remover functions were
// removed, nothing emits this event) and a listener with no emitter is
// dead weight, not defensive coding.

watch(() => props.franchiseId, () => {
  loadData();
});

onMounted(() => {
  loadData();
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('so-catalog-fiscal.saved', handleSaved);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('so-catalog-fiscal.saved', handleSaved);
});

defineExpose({ loadData });
</script>

<style scoped>
.selection-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: var(--p-highlight-background);
  border-radius: var(--p-border-radius);
}

.selection-count {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-highlight-color);
}

.selection-large-notice {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
}

.so-catalog-fiscal-hint,
.so-catalog-fiscal-empty,
.so-catalog-fiscal-error {
  padding: 1rem;
  color: var(--p-text-muted-color);
}

.so-catalog-fiscal-error {
  color: var(--p-red-500);
}
</style>
